import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, ButtonGroup, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

export interface ImportTemplateOption {
    /** Already-translated menu label. */
    label: string;
    onClick: () => void;
    /** Optional leading icon; defaults to the download icon (templates). */
    icon?: ReactNode;
}

interface ImportSplitButtonProps {
    /** Opens the import dialog. */
    onImport: () => void;
    /** Disables ONLY the Import action (e.g. no budget version yet). The template menu stays usable. */
    importDisabled?: boolean;
    /** Template downloads listed in the caret menu. */
    templateOptions: ImportTemplateOption[];
    /** Disables the caret/menu (e.g. while a template download is in flight). */
    templatesDisabled?: boolean;
}

/**
 * CALL-750: split button — the main segment opens the import dialog (version-gated by the caller where
 * relevant); the caret opens a menu of downloadable import templates. The caret is intentionally
 * independent of `importDisabled`: templates are version-agnostic, so a user can grab the expected
 * format before they have a budget version or data to import. The same templates also live inside the
 * import dialog itself — this is just a second, faster way to reach them from the toolbar.
 */
const ImportSplitButton = ({
    onImport,
    importDisabled,
    templateOptions,
    templatesDisabled,
}: ImportSplitButtonProps) => {
    const { t } = useTranslation('budget');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <ButtonGroup variant="outlined" size="small">
                <Button startIcon={<UploadFileIcon />} onClick={onImport} disabled={importDisabled}>
                    <span>{t('action.import')}</span>
                </Button>
                <Button
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    disabled={templatesDisabled || templateOptions.length === 0}
                    aria-label={t('import.downloadTemplate')}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    sx={{ px: 0, minWidth: 32 }}
                >
                    <ArrowDropDownIcon fontSize="small" />
                </Button>
            </ButtonGroup>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {templateOptions.map((option) => (
                    <MenuItem
                        key={option.label}
                        onClick={() => {
                            setAnchorEl(null);
                            option.onClick();
                        }}
                    >
                        <ListItemIcon>{option.icon ?? <DownloadIcon fontSize="small" />}</ListItemIcon>
                        <ListItemText>{option.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default ImportSplitButton;
